import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateReductions } from "state/orderEdition/actions";
import { Form, Button, Row, Col } from "react-bootstrap";
import CurrencyComponent from './CurrencyComponent';
import ReductionComponentLegacy from "./ReductionComponentLegacy";

// Types de réduction (doivent matcher les constantes de l'entité Promotion).
const TYPE_OPTIONS = [
  { value: "commercial", label: "Commercial" },
  { value: "sav", label: "SAV" },
];

// Taux de repli utilisé tant que la prestation ne contient aucune ligne : au-delà,
// le serveur ventile la remise au prorata des taux de TVA réellement présents
// (Cart::getPromotionTaxBreakdown).
const TVA_FALLBACK = 10;

// Repli à référence stable (voir le useMemo plus bas).
const REDUCTIONS_VIDES = [];

const reductionVide = () => ({
  label: "",
  type: "commercial",
  direct: null,
  proportional: null,
  tva: TVA_FALLBACK,
});

const ReductionMultiple = ({ orderIndex }) => {
  const dispatch = useDispatch();

  // On sélectionne la commande (référence stable venant du store) et on dérive la liste
  // avec useMemo : construire le tableau dans le sélecteur renvoyait une nouvelle
  // référence à chaque rendu, donc un re-rendu à chaque action du store.
  const order = useSelector((state) => state.orderEdition.orders[orderIndex]);

  // Liste des réductions, avec repli sur l'ancien champ unique `reduction`.
  const reductions = useMemo(() => {
    if (!order) {
      return REDUCTIONS_VIDES;
    }
    if (Array.isArray(order.reductions)) {
      return order.reductions;
    }
    if (order.reduction && (order.reduction.direct || order.reduction.proportional)) {
      return [{ ...reductionVide(), ...order.reduction, type: order.reduction.type || "commercial" }];
    }
    return REDUCTIONS_VIDES;
  }, [order]);

  const commit = (next) => {
    dispatch(updateReductions(orderIndex, next));
  };

  const handleAdd = () => {
    commit([...(reductions || []), reductionVide()]);
  };

  const handleRemove = (index) => {
    commit(reductions.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    const next = reductions.map((r, i) => {
      if (i !== index) {
        return r;
      }
      const updated = { ...r, [field]: value };
      // Réduction directe et proportionnelle mutuellement exclusives.
      if (field === "direct" && value !== "" && value !== null && value !== undefined && value !== 0) {
        updated.proportional = null;
      }
      if (field === "proportional" && value !== "" && value !== null && value !== undefined && Number(value) !== 0) {
        updated.direct = null;
      }
      return updated;
    });
    commit(next);
  };

  return (
    <div>
      {(!reductions || reductions.length === 0) && (
        <p className="text-muted mb-2" style={{ fontSize: "0.9rem" }}>
          Aucune réduction.
        </p>
      )}

      {(reductions || []).map((reduction, index) => {
        const reductionType = reduction.direct ? "direct" : reduction.proportional ? "proportional" : null;
        return (
          <div
            key={index}
            className="border rounded p-2 mb-2"
            style={{ backgroundColor: "#fafafa" }}
          >
            <Row className="align-items-end">
              <Col xs={6} md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Type</Form.Label>
                  <Form.Control
                    as="select"
                    value={reduction.type || "commercial"}
                    onChange={(e) => handleChange(index, "type", e.target.value)}
                  >
                    {TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col xs={6} md={4}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">Libellé</Form.Label>
                  <Form.Control
                    placeholder="Libellé"
                    value={reduction.label || ""}
                    onChange={(e) => handleChange(index, "label", e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={3}>
                <Form.Group className="mb-2">
                  <Form.Label className="mb-1">TVA</Form.Label>
                  <Form.Control
                    plaintext
                    readOnly
                    value="Ventilée automatiquement"
                    title="La remise est répartie par le serveur au prorata des taux de TVA réellement présents dans la prestation. Le taux ci-dessous ne sert que de repli si la prestation est encore vide."
                  />
                </Form.Group>
              </Col>

              <Col xs={6} md={2} className="text-right">
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="mb-2"
                  onClick={() => handleRemove(index)}
                >
                  Supprimer
                </Button>
              </Col>
            </Row>

            <Row>
              <Col xs={6}>
                <Form.Group className="mb-1">
                  <Form.Label className="mb-1">Réduction directe</Form.Label>
                  <CurrencyComponent
                    initialPrice={reduction.direct}
                    onPriceChange={(newValue) => handleChange(index, "direct", newValue)}
                    disabled={reductionType === "proportional"}
                    style={reductionType === "proportional" ? { backgroundColor: '#cccccc' } : {}}
                    nullable={true}
                  />
                </Form.Group>
              </Col>

              <Col xs={6}>
                <Form.Group className="mb-1">
                  <Form.Label className="mb-1">Réduction proportionnelle (%)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="Pourcentage"
                    value={reduction.proportional || ""}
                    onChange={(e) => handleChange(index, "proportional", e.target.value)}
                    disabled={reductionType === "direct"}
                    style={reductionType === "direct" ? { backgroundColor: '#cccccc' } : {}}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        );
      })}

      <Button variant="outline-primary" size="sm" onClick={handleAdd}>
        + Ajouter une réduction
      </Button>
    </div>
  );
};

/**
 * Aiguillage : réductions multiples typées + TVA masquées en prod tant que la
 * feature n'est pas activée → on garde l'UI classique (réduction unique).
 * Le wrapper n'appelle aucun hook : chaque variante gère les siens.
 */
const ReductionComponent = ({ orderIndex }) => {
  if (process.env.REACT_APP_ENV === "prod") {
    return <ReductionComponentLegacy orderIndex={orderIndex} />;
  }
  return <ReductionMultiple orderIndex={orderIndex} />;
};

export default ReductionComponent;
